/**
 * Deep Link Handler
 *
 * Centralized deep link handling for the JD Simplified mobile app.
 *
 * ⚠️ SINGLE HANDLER ARCHITECTURE
 *
 * This module should be the ONLY place that parses and routes deep links.
 * Do NOT add additional URL handlers in other files to avoid divergent
 * validation logic.
 *
 * Supported URL formats:
 * - Universal Links: https://jdsimplified.com/chat/abc123
 * - Custom Scheme: jdsimplified://chat/abc123
 * - Auth Callbacks: jdsimplified://auth/callback?mode=verifyEmail&oobCode=...
 *
 * @see docs/mobile-app/DEEP-LINKING.md for configuration details
 */

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isNativeApp } from './index';
import { logger } from '@/lib/logger';

// Navigation callback type
type NavigateFunction = (path: string) => void;

// Store the navigate function
let navigateHandler: NavigateFunction | null = null;

// Track if deep links have been initialized
let initialized = false;

/**
 * Initialize deep link handling.
 *
 * Call this from App.tsx after the router is available, passing the
 * navigate function.
 *
 * @param navigate - React Router's navigate function
 *
 * @example
 * // In App.tsx:
 * const navigate = useNavigate();
 * useEffect(() => {
 *   initializeDeepLinks(navigate);
 * }, [navigate]);
 */
export function initializeDeepLinks(navigate: NavigateFunction): void {
  if (!isNativeApp) {
    logger.debug('[DeepLinks] Not a native platform, skipping initialization');
    return;
  }

  if (initialized) {
    logger.debug('[DeepLinks] Already initialized, updating navigate handler');
    navigateHandler = navigate;
    return;
  }

  navigateHandler = navigate;
  initialized = true;

  logger.info('[DeepLinks] Initializing deep link handler...');

  // Listen for deep links when app is open
  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    logger.info('[DeepLinks] Received URL', { url: event.url });
    handleDeepLink(event.url);
  });

  // Check if app was opened with a deep link (cold start)
  App.getLaunchUrl().then(result => {
    if (result?.url) {
      logger.info('[DeepLinks] App launched with URL', { url: result.url });
      handleDeepLink(result.url);
    }
  });

  logger.info('[DeepLinks] Initialization complete');
}

/**
 * Allowed hosts for deep links.
 *
 * NOTE: Empty string '' is valid for custom scheme URLs (jdsimplified://)
 * because they parse with an empty host.
 */
const ALLOWED_HOSTS = [
  'jdsimplified.com',
  'dev.jdsimplified.com',
  'api.jdsimplified.com',
  '', // Custom scheme URLs have empty host
];

/**
 * Process an incoming deep link URL.
 */
function handleDeepLink(url: string): void {
  try {
    const urlObj = new URL(url);
    const isCustomScheme = urlObj.protocol === 'jdsimplified:';

    logger.debug('[DeepLinks] Parsing URL:', {
      url,
      protocol: urlObj.protocol,
      host: urlObj.host,
      pathname: urlObj.pathname,
      hash: urlObj.hash,
      search: urlObj.search
    });

    // Validate host for non-custom-scheme URLs
    if (!isCustomScheme && !ALLOWED_HOSTS.includes(urlObj.host)) {
      logger.warn('[DeepLinks] Rejected URL from unauthorized host', { host: urlObj.host });
      return;
    }

    // Handle Firebase auth action URLs (email verification, password reset)
    if (isAuthCallback(urlObj)) {
      handleAuthCallback(urlObj);
      return;
    }

    // Handle regular deep link navigation
    const path = normalizePath(urlObj, isCustomScheme);
    logger.info('[DeepLinks] Navigating to', { path });

    if (navigateHandler) {
      navigateHandler(path);
    } else {
      logger.error('[DeepLinks] Navigate handler not set. Call initializeDeepLinks() first.');
    }
  } catch (error) {
    logger.error('[DeepLinks] Error parsing URL', error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Check if URL is a Firebase auth action callback.
 * Firebase uses ?mode=verifyEmail|resetPassword|recoverEmail&oobCode=...
 */
function isAuthCallback(urlObj: URL): boolean {
  return (
    urlObj.pathname.includes('/auth/callback') ||
    (urlObj.searchParams.has('mode') && urlObj.searchParams.has('oobCode'))
  );
}

/**
 * Normalize the path from a deep link URL.
 *
 * Custom scheme URLs (jdsimplified://path) parse differently than
 * standard URLs (https://host/path), so we need to handle both.
 */
function normalizePath(urlObj: URL, isCustomScheme: boolean): string {
  let path = urlObj.pathname;

  if (isCustomScheme && urlObj.host) {
    // Custom scheme: host becomes first path segment
    // e.g., jdsimplified://chat/123 → host='chat', pathname='/123'
    path = '/' + urlObj.host + urlObj.pathname;
  }

  // Normalize multiple leading slashes
  path = path.replace(/^\/+/, '/');

  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  return path;
}

/**
 * Handle Firebase auth action callback.
 *
 * Firebase action URLs contain:
 * - mode: verifyEmail | resetPassword | recoverEmail
 * - oobCode: one-time action code
 * - continueUrl (optional): where to redirect after
 */
function handleAuthCallback(urlObj: URL): void {
  const mode = urlObj.searchParams.get('mode');
  logger.info('[DeepLinks] Processing auth callback', { mode });

  if (!navigateHandler) {
    logger.error('[DeepLinks] Navigate handler not set for auth callback');
    return;
  }

  // Route to the auth callback page with query params preserved
  const callbackPath = `/auth/callback${urlObj.search}`;
  navigateHandler(callbackPath);
}

/**
 * Generate a shareable deep link URL.
 * Returns a universal link that works on web and opens app if installed.
 *
 * @param path - The path to link to (e.g., '/chat/abc123')
 */
export function generateDeepLink(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return `https://jdsimplified.com${normalizedPath}`;
}

/**
 * Generate an app-only deep link.
 * Use this when you specifically want to open the native app.
 *
 * @param path - The path to link to (e.g., 'chat/abc123')
 */
export function generateAppLink(path: string): string {
  // Remove leading slash for custom scheme
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `jdsimplified://${normalizedPath}`;
}
